/* eslint-disable no-magic-numbers */
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import {
  Door,
  DoorBatteryWarningLevel,
  LocationInput,
  Mutation,
  MutationAddLocationArgs,
  MutationDeleteDoorByIdArgs,
  MutationDeleteLocationArgs,
  MutationUpdateDoorArgs,
  MutationUpdateLocationArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import {
  ADD_LOCATION,
  DELETE_DOOR_BY_ID,
  DELETE_LOCATION,
  UPDATE_DOOR_WITH_LOCATIONS,
  UPDATE_LOCATION
} from 'graphql/mutations';
import { GET_DOORS_FOR_TENANT_WITH_LOCATIONS, GET_LOCATIONS_WITH_DOORS_FOR_TENANT } from 'graphql/queries';
import { useEffect } from 'react';
import {
  deleteDoorWithLocationsAction,
  setDoorWithLocationsAction,
  updateDoorWithLocationsAction
} from 'state/actions/doorsWithLocations';
import {
  addLocationsWithDoorsAction,
  deleteLocationsWithDoorsAction,
  setLocationsWithDoorsAction,
  updateLocationsWithDoorsAction
} from 'state/actions/locationsWithDoors';
import { doorsWithLocationsVar, locationsAndDoorsRefetchVar, locationsWithDoorsVar } from 'state/vars';
import ArrayUtil from 'utils/Array/Array.util';
import { BaseHookProps } from '../shared/types';
dayjs.extend(weekday);

const overwriteBatteryStatus = (doors: Door[]) =>
  doors.map((door) => {
    const firstTwoChars = door.hardwareIdentifier?.slice(0, 2);
    switch (firstTwoChars) {
      case '65':
      case '66':
        return { ...door, batteryWarningLevel: DoorBatteryWarningLevel.Powered };
      default:
        return door;
    }
  });

const useDoorsAndLocations = ({ handleFetchError }: BaseHookProps) => {
  const doorsWithLocations = useReactiveVar(doorsWithLocationsVar);
  const locationsWithDoors = useReactiveVar(locationsWithDoorsVar);
  const locationsAndDoorsRefetch = useReactiveVar(locationsAndDoorsRefetchVar);

  const getLocationsOfDoor = (doorId: string) =>
    locationsWithDoors.filter((location) => location.doors.some((door) => door.id === doorId));

  const [getDoorsWithLocationsForTenant, { loading: doorsWithLocationsLoading }] = useLazyQuery<
    Pick<Query, 'getDoorsForTenant'>
  >(GET_DOORS_FOR_TENANT_WITH_LOCATIONS, {
    onCompleted: (data) => {
      const doors = overwriteBatteryStatus(data.getDoorsForTenant);
      const doorsComplete = doors.map(
        (door): Door => ({
          ...door,
          locations: door.locations.map((location) => ({
            ...location,
            doors: doors.filter((locationDoor) =>
              locationDoor.locations.some((doorLocation) => doorLocation.id === location.id)
            )
          }))
        })
      );
      setDoorWithLocationsAction(doorsComplete);
    },
    onError: () => {
      handleFetchError('Error while fetching doors');
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    getDoorsWithLocationsForTenant();
  }, [getDoorsWithLocationsForTenant]);

  const [getLocationsWithDoorsForTenant, { loading: locationsWithDoorsLoading }] = useLazyQuery<
    Pick<Query, 'getLocationsForTenant'>
  >(GET_LOCATIONS_WITH_DOORS_FOR_TENANT, {
    onCompleted: (data) => {
      setLocationsWithDoorsAction(data.getLocationsForTenant);
    },
    onError: () => {
      handleFetchError('Error while fetching locations');
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    getLocationsWithDoorsForTenant();
  }, [getLocationsWithDoorsForTenant]);

  useEffect(() => {
    if (locationsAndDoorsRefetch === true) {
      getDoorsWithLocationsForTenant();
      getLocationsWithDoorsForTenant();
      locationsAndDoorsRefetchVar(false);
    }
  }, [getDoorsWithLocationsForTenant, getLocationsWithDoorsForTenant, locationsAndDoorsRefetch]);

  const [updateDoorWithLocationsMutation, { loading: updateDoorWithLocationsLoading }] = useMutation<
    Pick<Mutation, 'updateDoor'>,
    MutationUpdateDoorArgs
  >(UPDATE_DOOR_WITH_LOCATIONS, {
    onCompleted: (data) => {
      if (data && data.updateDoor) {
        const updatedDoor = overwriteBatteryStatus([data.updateDoor])[0];
        const updateDoorComplete: Door = {
          ...updatedDoor,
          locations: updatedDoor.locations.map((location) => ({
            ...location,
            doors: []
          }))
        };
        updateDoorWithLocationsAction(updateDoorComplete);
      }
      locationsAndDoorsRefetchVar(true);
    },
    onError: () => {
      handleFetchError('Error while updating doors');
    }
  });

  const [deleteDoorWithLocationsMutation, { loading: deleteDoorWithLocationsLoading }] = useMutation<
    Pick<Mutation, 'deleteDoorById'>,
    MutationDeleteDoorByIdArgs
  >(DELETE_DOOR_BY_ID, {
    onCompleted: (data) => {
      if (data.deleteDoorById) {
        deleteDoorWithLocationsAction(data.deleteDoorById);
      }
      locationsAndDoorsRefetchVar(true);
    },
    onError: () => {
      handleFetchError('Error while deleting doors');
    }
  });
  const updateDoorWithLocations = async (
    doorId: string,
    name: string,
    locationIds?: string[] | null,
    officeModeFrom?: string,
    officeModeTo?: string
  ) => {
    const { data } = await updateDoorWithLocationsMutation({
      variables: {
        doorId,
        name,
        locationIds,
        officeMode: {
          scheduleType: 'weekSchedule',
          weekSchedule:
            officeModeFrom && officeModeTo
              ? ArrayUtil.Range(7).map((_, index) => ({
                  dayName: dayjs().weekday(index).format('dddd').toLowerCase(),
                  from: officeModeFrom,
                  to: officeModeTo
                }))
              : []
        }
      }
    });
    return data;
  };

  const deleteDoor = async (doorId: string) => {
    const { data } = await deleteDoorWithLocationsMutation({
      variables: {
        doorId
      }
    });
    return data;
  };

  const isOfficeMode = (door: Door) => door.officeMode.weekDays.some((day) => day !== null);

  const [addLocationMutation, { loading: addLocationLoading }] = useMutation<
    Pick<Mutation, 'addLocation'>,
    MutationAddLocationArgs
  >(ADD_LOCATION, {
    onCompleted: (data) => {
      addLocationsWithDoorsAction(data.addLocation);
      locationsAndDoorsRefetchVar(true);
    },
    onError: () => {
      handleFetchError('Error while adding location');
    }
  });

  const [updateLocationMutation, { loading: updateLocationLoading }] = useMutation<
    Pick<Mutation, 'updateLocation'>,
    MutationUpdateLocationArgs
  >(UPDATE_LOCATION, {
    onCompleted: (data) => {
      if (data && data.updateLocation) updateLocationsWithDoorsAction(data.updateLocation);
      locationsAndDoorsRefetchVar(true);
    },

    onError: () => {
      handleFetchError('Error while updating location');
    }
  });

  const [deleteLocationMutation, { loading: deleteLocationLoading }] = useMutation<
    Pick<Mutation, 'deleteLocation'>,
    MutationDeleteLocationArgs
  >(DELETE_LOCATION, {
    onCompleted: (data) => {
      if (data.deleteLocation) {
        deleteLocationsWithDoorsAction(data.deleteLocation.id);
      }
      locationsAndDoorsRefetchVar(true);
    },
    onError: () => {
      handleFetchError('Error while deleting location');
    }
  });

  const addLocation = async (location: LocationInput) => {
    const { data } = await addLocationMutation({
      variables: {
        location
      }
    });
    return data;
  };

  const updateLocation = async (id: string, location: LocationInput) => {
    const { data } = await updateLocationMutation({
      variables: {
        id,
        location
      }
    });
    return data;
  };

  const deleteLocation = async (id: string) => {
    const { data } = await deleteLocationMutation({
      variables: {
        id
      }
    });
    return data;
  };

  return {
    doorsWithLocations,
    doorsWithLocationsLoading,
    getLocationsWithDoorsForTenant,
    getDoorsWithLocationsForTenant,
    updateDoorWithLocations,
    updateDoorWithLocationsLoading,
    deleteDoor,
    deleteDoorWithLocationsLoading,
    isOfficeMode,
    locationsWithDoors,
    locationsWithDoorsLoading,
    getLocationsOfDoor,
    addLocation,
    addLocationLoading,
    updateLocation,
    updateLocationLoading,
    deleteLocation,
    deleteLocationLoading
  };
};

export default useDoorsAndLocations;
